// Central connectivity manager: browser online/offline events + backend reachability probe (/api/health).
// States: ONLINE | OFFLINE | RECONNECTING | SYNCING | SYNC_ERROR. Never relies on navigator.onLine alone.
import { useEffect, useState } from "react";
import api from "@/lib/api";

const state = { status: typeof navigator !== "undefined" && navigator.onLine === false ? "OFFLINE" : "ONLINE", pending: 0, lastSync: null, message: "" };
const listeners = new Set();
const emit = () => listeners.forEach((l) => l({ ...state }));
export const setConn = (patch) => { Object.assign(state, patch); emit(); };
export const getConn = () => ({ ...state });

let probing = false;
export async function probe() {
  if (probing) return state.status;
  probing = true;
  try {
    await api.get("/health", { timeout: 4000 });
    if (state.status !== "ONLINE" && state.status !== "SYNCING") setConn({ status: "ONLINE", message: "" });
    return "ONLINE";
  } catch (e) {
    if (!e.response) { if (state.status === "ONLINE") setConn({ status: "OFFLINE", message: "" }); return "OFFLINE"; }
    return "ONLINE"; // backend answered (even 4xx) -> reachable
  } finally { probing = false; }
}

// Called by the api interceptor on network failures so a dropped connection is detected mid-action.
export function reportNetworkError() { if (state.status === "ONLINE") { setConn({ status: "OFFLINE" }); } }

let started = false;
export function startConnectivity() {
  if (started || typeof window === "undefined") return;
  started = true;
  window.addEventListener("offline", () => setConn({ status: "OFFLINE" }));
  window.addEventListener("online", () => { setConn({ status: "RECONNECTING" }); probe(); });
  setInterval(() => { if (state.status === "OFFLINE" || state.status === "RECONNECTING") probe(); }, 5000);
  setInterval(() => { if (state.status === "ONLINE") probe(); }, 30000);
}

export function useConnectivity() {
  const [s, setS] = useState(getConn());
  useEffect(() => { startConnectivity(); listeners.add(setS); return () => listeners.delete(setS); }, []);
  return s;
}
export const isOffline = () => state.status !== "ONLINE" && state.status !== "SYNCING";
